import React, { useEffect, useMemo, useState } from "react";
import PageBody from "Common/Custom/PageBody";
import { HeadingOne } from "Common/Custom/HeadingOne";
import FormSelect from "Common/Custom/FormSelect";
import FormInput from "Common/Custom/FormInput";
import FormSubmitButton from "Common/Custom/FormSubmitButton";
import AddCustomerModal from "./AddCustomerModal";
import FormNumberInput from "Common/Custom/FormNumberInput";
import PropTypes from "prop-types";

const AddPoolBookingForm = ({
    fields = [],
    values = {},
    errors = {},
    handleChange = () => { },
    handleSubmit = () => { },
    loading = false,
    PooNameOptions = [],
    CustomerOpctions = [],
    OldCustomerOptions = [],
    newcustomer = [],
    onCustomerSave = () => { },
}) => {
    const [showModal, setShowModal] = useState(false);

    const isPackageSelected = Boolean(values.p_pack_id);

    /* ---------- AUTO TOTAL ---------- */
    useEffect(() => {
        if (!values.price) return;
        const totalAmount = Number(values.price) * Number(values.units);
        // Only update if value actually changed — avoids infinite loop
        if (Number(values.amount) !== totalAmount) {
            handleChange({ target: { name: "amount", value: totalAmount } });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.price, values.units]);

    /* ---------- OPTIONS ---------- */
    const poolOptions = useMemo(
        () =>
            PooNameOptions.map(p => ({
                value: p.pool_package_id,
                label: p.pool_package_name,
            })),
        [PooNameOptions]
    );

    const customerOptions = useMemo(
        () =>
            CustomerOpctions.map(c => ({
                value: c.customer_id,
                label: c.customer_name,
            })),
        [CustomerOpctions]
    );

    /* ---------- FIELD RENDER ---------- */
    const renderField = (field) => {
        const isCustomer = field.name === "customer_id";
        const isPackage = field.name === "p_pack_id";
        const isPrice = field.name === "price";
        const isAmount = field.name === "amount";
        const isUnits = field.name === "units";
        const isPaymentStatus = field.name === "payment_status";

        // Base disabled logic
        let isDisabled =
            (isCustomer && !isPackageSelected) || isPrice || isAmount || field.disabled;

        // Payment Status specific logic
        if (isPaymentStatus) {
            isDisabled = !values.p_pack_id || !values.customer_id || !values.units;

            // Set default value to 'unpaid' if not already set
            if (!values.payment_status) {
                handleChange({
                    target: {
                        name: "payment_status",
                        value: "unpaid",
                    },
                });
            }
        }

        const getSelectOptions = () => {
            switch (field.name) {
                case "customer_id":
                    return customerOptions;

                case "p_pack_id":
                    return poolOptions;

                case "payment_status":
                case "booking_status":
                    return field.options || [];

                default:
                    return [];
            }
        };

        /** SELECT FIELD */
        if (field.type === "select") {
            return (
                <FormSelect
                    key={field.name}
                    field={field}
                    disabled={isDisabled}
                    value={values[field.name]}
                    handleChange={handleChange}
                    options={getSelectOptions()}
                    error={errors[field.name]}
                />
            );
        }

        /** UNITS (NUMBER INPUT) */
        if (isUnits) {
            return (
                <FormInput
                    key={field.name}
                    field={{ ...field, disabled: !isPackageSelected }}
                    value={values[field.name]}
                    handleChange={handleChange}
                    error={errors[field.name]}
                />
            );
        }

        /** DEFAULT INPUT */
        return (
            <FormInput
                key={field.name}
                field={{ ...field, disabled: isDisabled }}
                value={values[field.name] || ""}
                handleChange={handleChange}
                error={errors[field.name]}
            />
        );
    };



    return (
        <>
            <PageBody
                headerchildren={
                    <>
                        <HeadingOne>Create Pool Booking</HeadingOne>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => setShowModal(true)}
                            disabled={!isPackageSelected}
                        >
                            {values?.customer_name ? "Edit Customer" : "Add Customer"}
                        </button>
                    </>
                }
                onSubmit={handleSubmit}
            >
                {fields.map(renderField)}

                <FormSubmitButton
                    label="Create"
                    loading={loading}
                    colSize="col-12"
                    align="end"
                    disabled={
                        !values.p_pack_id ||
                        !values.customer_id ||
                        !values.units ||
                        values.units <= 0 ||
                        loading
                    }
                />
            </PageBody>

            <AddCustomerModal
                show={showModal}
                onHide={() => setShowModal(false)}
                OldCustomerOptions={OldCustomerOptions}
                newcustomer={newcustomer}
                onSave={onCustomerSave}
                errors={errors}
            />
        </>
    );
};

export default AddPoolBookingForm;
